import { useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import { useDocumentTitle } from '@leagueplatform/web-common';
import {
  LoadingIndicator,
  ErrorState,
} from '@leagueplatform/web-common-components';
import { SettingsPageLayout } from 'components/settings-page-layout/settings-page-layout.component';
import { CommunicationPreferencesForm } from './communication-preferences-form.component';
import { useCommunicationPreferencesQuery } from './use-communication-preferences-query.hook';
import { useSaveCommunicationPreferences } from './use-save-communication-preferences.hook';
import { buildPatchDelta } from './communication-preferences.mappers';

export const CommunicationPreferencesPage = () => {
  const { formatMessage } = useIntl();
  usePageViewAnalytics({ screen_name: 'Communication Preferences' });
  useDocumentTitle(formatMessage({ id: 'STR_COMMUNICATION_PREFERENCES' }));

  const { data, isLoading, isError } = useCommunicationPreferencesQuery();
  const saveMutation = useSaveCommunicationPreferences();

  // Bumped on save success to remount the form, re-seeding it from the saved
  // server data and resetting the dirty snapshot (Save returns to disabled).
  // Keyed on this rather than the query's dataUpdatedAt so a background refetch
  // can't remount mid-edit and discard unsaved changes.
  const [formSeedKey, setFormSeedKey] = useState(0);

  return (
    <SettingsPageLayout
      heading={formatMessage({ id: 'STR_COMMUNICATION_PREFERENCES' })}
      spacing="$one"
    >
      {isLoading && <LoadingIndicator />}

      {/* Only on an initial-load failure (no data to seed). A failed
          background refetch keeps the last good data cached, so the seeded
          form stays usable; save failures surface as a toast instead. */}
      {isError && !data && (
        <ErrorState
          headingString={formatMessage({
            id: 'STR_ERROR_STATE_HEADING_GENERIC',
          })}
          bodyString={formatMessage({ id: 'STR_ERROR_STATE_BODY_API' })}
          buttonText={formatMessage({ id: 'STR_RELOAD' })}
          error={null}
        />
      )}

      {data && (
        <CommunicationPreferencesForm
          key={formSeedKey}
          initialValues={data}
          onSave={(current, seeded) =>
            // The form hands back the values it was seeded with at mount, so the
            // delta diffs against that seed — not the latest query data, which a
            // background refetch can change mid-edit. onSuccess runs after the
            // hook's invalidation refetch resolves, so the form re-mounts and
            // re-seeds from the saved server state.
            saveMutation.mutate(buildPatchDelta(seeded, current), {
              onSuccess: () => setFormSeedKey((key) => key + 1),
            })
          }
          isSaving={saveMutation.isLoading}
        />
      )}
    </SettingsPageLayout>
  );
};
